import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { modalAnimation } from '@shared/animations';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './modal.component.html',
  animations: [modalAnimation]
})
export class ModalComponent {
  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() iconType: 'success' | 'error' | 'warning' | 'info' | 'question' | 'custom' = 'info';
  @Input() customIcon?: string;
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdropClick: boolean = true;
  @Input() primaryButtonText: string = '';
  @Input() secondaryButtonText: string = '';
  @Input() showPrimaryButton: boolean = true;
  @Input() showSecondaryButton: boolean = true;
  @Input() primaryButtonClass: string = 'bg-purple-600 hover:bg-purple-700 text-white';
  @Input() secondaryButtonClass: string = 'border border-gray-300 text-gray-700 hover:bg-gray-50';

  @Output() close = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();
  @Output() backdropClick = new EventEmitter<void>();

  get modalClasses(): string {
    const sizes = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      full: 'max-w-full mx-4'
    };
    return `relative bg-white rounded-lg shadow-xl ${sizes[this.size]} w-full p-6 transform transition-all`;
  }

  get iconContainerClasses(): string {
    const colors = {
      success: 'bg-green-100',
      error: 'bg-red-100',
      warning: 'bg-yellow-100',
      info: 'bg-blue-100',
      question: 'bg-purple-100',
      custom: 'bg-gray-100'
    };
    return `mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${colors[this.iconType]}`;
  }

  get iconClasses(): string {
    const colors = {
      success: 'text-green-600',
      error: 'text-red-600',
      warning: 'text-yellow-600',
      info: 'text-blue-600',
      question: 'text-purple-600',
      custom: 'text-gray-600'
    };
    return `h-6 w-6 ${colors[this.iconType]}`;
  }

  get iconPath(): string {
    const paths = {
      success: 'M5 13l4 4L19 7',
      error: 'M6 18L18 6M6 6l12 12',
      warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      question: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      custom: this.customIcon || ''
    };
    return paths[this.iconType];
  }

  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(): void {
    if (this.closeOnBackdropClick) {
      this.close.emit();
    }
    this.backdropClick.emit();
  }

  onPrimaryAction(): void {
    this.primaryAction.emit();
  }

  onSecondaryAction(): void {
    this.secondaryAction.emit();
  }
}
